/**
 * Turning the microphone up, which is the one direction nothing else can.
 *
 * Every other volume control an operator has - the slider on the speaker, the
 * one on the ring, the machine's own - attenuates. A headset that is simply too
 * quiet at the far end has no answer in any of them, and "they say they can
 * barely hear me" is the single most common thing said on a call this CRM
 * places. So the input gets a gain node of its own, and it goes above unity.
 *
 * THE SDK'S OWN DOOR, not a private field. Twilio's `AudioHelper.addProcessor`
 * exists for exactly this (the SDK's example uses it to swap the microphone for
 * background music) and the SDK re-runs the processor whenever the input stream
 * changes - a device switched mid-call, a call started on the system default.
 * Reaching into the peer connection would have to be redone at every one of
 * those moments and would break on an SDK upgrade.
 *
 * ⚠ IT FAILS OPEN, ALWAYS, AND THAT IS THE MOST IMPORTANT LINE IN THE FILE. A
 * processor sits between the microphone and the far end: if it hands back a
 * stream that is not carrying audio, the call is up, the timer runs, everything
 * looks perfect and nobody can hear the operator. An `AudioContext` can refuse
 * to start (autoplay policy on a page with no gesture behind it) and can be
 * suspended by the OS afterwards, so every path that cannot produce a working
 * processed stream returns the ORIGINAL stream instead - unamplified, which is
 * the state the whole CRM had a week ago, rather than silent.
 *
 * That is also why the processor is not installed at all while the level is 1.
 * The default costs nothing and risks nothing; the risk is opted into by an
 * operator who has just dragged a slider, which is a gesture, which is what an
 * `AudioContext` needs to start.
 */

import { audioContextCtor } from "./context";

/** The shape `AudioHelper` expects of a processor. Structural, so no import. */
export interface AudioProcessorLike {
  createProcessedStream(stream: MediaStream): Promise<MediaStream>;
  destroyProcessedStream(stream: MediaStream): Promise<void>;
}

/** The half of `AudioHelper` that takes processors. */
export interface AudioProcessorHost {
  addProcessor(processor: AudioProcessorLike, isRemote?: boolean): Promise<void>;
  removeProcessor(processor: AudioProcessorLike, isRemote?: boolean): Promise<void>;
}

/**
 * Below this a level counts as "unchanged".
 *
 * The slider moves in whole percent, so nothing legitimate lands inside it. It
 * is here because floating point does: 140% dragged back down arrives as
 * 0.9999999999999999 often enough to matter, and that number installing a whole
 * audio graph to multiply by one would be absurd.
 */
const UNITY_EPSILON = 0.005;

export const isUnityGain = (level: number): boolean => Math.abs(level - 1) < UNITY_EPSILON;

/** What `applyMicGain` should do, given the level asked for and what is fitted. */
export type MicGainStep = "install" | "set" | "none";

/**
 * The decision, without the audio.
 *
 * Pure so the interesting half can be read and tested on its own. Note what is
 * NOT here: there is no "take it off again". Removing a processor makes the SDK
 * re-acquire the microphone (`_restartInputStreams`), which is a gap in the
 * audio, so an operator dragging a slider back and forth across 100% would tear
 * their own call apart. Unity with one already fitted is a gain of exactly 1,
 * and the processor comes off when the call ends - see `releaseMicGain`.
 */
export function micGainStep(level: number, installed: boolean): MicGainStep {
  if (installed) return "set";
  return isUnityGain(level) ? "none" : "install";
}

/** The nodes standing behind one processed stream. */
interface Graph {
  source: MediaStreamAudioSourceNode;
  gain: GainNode;
  dest: MediaStreamAudioDestinationNode;
}

/**
 * One gain node per live input stream, all following the same level.
 *
 * More than one graph can exist at a time for a moment: the SDK builds the next
 * stream before it tears the previous one down when a device is switched
 * mid-call, and both are asked for by the same object.
 */
export class MicGainProcessor implements AudioProcessorLike {
  private ctx: AudioContext | null = null;
  private graphs = new Map<MediaStream, Graph>();
  private level = 1;

  /** The multiplier every live graph follows from now on. */
  setGain(level: number): void {
    this.level = level;
    for (const graph of this.graphs.values()) {
      // Ramped rather than assigned: a step change in gain on a live voice is
      // an audible click at the far end, which sounds like a connection fault.
      try {
        const now = graph.gain.context.currentTime;
        graph.gain.gain.setTargetAtTime(level, now, 0.02);
      } catch {
        graph.gain.gain.value = level;
      }
    }
  }

  async createProcessedStream(stream: MediaStream): Promise<MediaStream> {
    const ctx = await this.context();
    // No context, or one the browser will not start. The call goes ahead on the
    // raw microphone - see the warning at the top of the file.
    if (!ctx || ctx.state !== "running") return stream;
    try {
      const source = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain();
      gain.gain.value = this.level;
      const dest = ctx.createMediaStreamDestination();
      source.connect(gain);
      gain.connect(dest);
      // NOT connected to `ctx.destination`. Routing a live microphone to the
      // speakers is how a room starts howling.
      this.graphs.set(dest.stream, { source, gain, dest });
      return dest.stream;
    } catch {
      return stream;
    }
  }

  async destroyProcessedStream(stream: MediaStream): Promise<void> {
    const graph = this.graphs.get(stream);
    if (!graph) return;
    this.graphs.delete(stream);
    try {
      graph.source.disconnect();
      graph.gain.disconnect();
      graph.dest.disconnect();
    } catch {
      // Already torn down by a context that closed under it.
    }
  }

  /**
   * Let the context go.
   *
   * A context left open holds an audio device for the rest of the session, and
   * the tab keeps its recording indicator lit - the same reason `useInputLevel`
   * closes its own rather than suspending it.
   *
   * This is why the microphone keeps a context of its own instead of using the
   * shared output one from `context.ts`: that one is never closed, and a
   * never-closed context with a MICROPHONE in it would light the recording
   * indicator for the whole session. The rule differs because the direction
   * does.
   */
  async close(): Promise<void> {
    const ctx = this.ctx;
    this.ctx = null;
    this.graphs.clear();
    await ctx?.close().catch(() => {});
  }

  /** The context, built on first use and resumed whenever it drifts asleep. */
  private async context(): Promise<AudioContext | null> {
    if (!this.ctx) {
      const Ctor = audioContextCtor();
      if (!Ctor) return null;
      try {
        this.ctx = new Ctor();
      } catch {
        return null;
      }
    }
    if (this.ctx.state === "suspended") {
      await this.ctx.resume().catch(() => {});
    }
    return this.ctx;
  }
}

/**
 * The one processor this browser ever fits, and whether it is fitted.
 *
 * Module level rather than passed around, because the thing it is fitted TO is
 * module level in every practical sense: there is one Twilio `Device` per tab,
 * and `removeProcessor` refuses any object other than the one that was added.
 */
let processor: MicGainProcessor | null = null;
let fitted = false;
/**
 * An install that has been asked for and not yet answered.
 *
 * ⚠ THIS IS NOT AN OPTIMISATION. The caller is a slider, so `applyMicGain` is
 * called forty times in one drag, and fitting a processor takes a round trip
 * through the SDK (`addProcessor` restarts the input stream). Without this the
 * second tick sees `fitted` still false, asks for a second processor, is
 * refused - the SDK allows exactly one - and its catch writes `fitted = false`
 * AFTER the first tick's success has written true. From then on the module
 * believes nothing is fitted while something is: every later level change is
 * refused, and `releaseMicGain` skips the removal, so the gain is stuck at
 * whatever it was for the rest of the session.
 */
let installing: Promise<void> | null = null;

/** The `addProcessor` round trip, which never rejects. */
async function install(host: AudioProcessorHost, next: MicGainProcessor): Promise<void> {
  try {
    await host.addProcessor(next, false);
    fitted = true;
  } catch {
    // Another processor is already fitted, or the SDK refused the shape. The
    // call carries on unamplified rather than not carrying on.
    fitted = false;
    if (processor === next) processor = null;
  }
}

/**
 * Put the operator's microphone level onto the running device.
 *
 * A no-op when there is nothing to put it on. The dial pad's copy of the picker
 * passes null - the level is simply stored there, and `CallProvider` applies it
 * from storage at the moment the call is placed, exactly as the device choice
 * is applied.
 */
export async function applyMicGain(host: AudioProcessorHost | null, level: number): Promise<void> {
  if (!host) return;

  if (!installing && micGainStep(level, fitted) === "install") {
    const next = new MicGainProcessor();
    processor = next;
    // Cleared through the variable rather than inside `install`, and only if
    // this attempt is still the current one: a `finally` inside `install` would
    // run before the assignment below on a synchronous refusal, leaving a
    // resolved promise parked here forever and no install ever possible again.
    const attempt = install(host, next).finally(() => {
      if (installing === attempt) installing = null;
    });
    installing = attempt;
  }

  // Fitted, on its way, or not wanted at all - the level lands on the processor
  // object either way, and `createProcessedStream` reads it when it builds.
  processor?.setGain(level);
  await installing;
}

/** Whether a processor is fitted to the running device right now. */
export const micGainFitted = (): boolean => fitted;

/**
 * Take the processor off: the call is over.
 *
 * Called beside `releaseAudioInput`, at the one moment when re-acquiring the
 * microphone costs nothing because nobody is talking into it. Safe with a null
 * host, which is how the state is cleared when the device itself has gone.
 */
export async function releaseMicGain(host: AudioProcessorHost | null): Promise<void> {
  // An install still in flight is finished first, or it would fit a processor
  // onto a device this call is in the middle of letting go of.
  await installing;
  const current = processor;
  const wasFitted = fitted;
  processor = null;
  fitted = false;
  installing = null;
  if (!current) return;
  if (wasFitted && host) {
    try {
      await host.removeProcessor(current, false);
    } catch {
      // Never added, or the device is already gone.
    }
  }
  await current.close();
}
